// caiet UC92-03 and ACCS-09: the IMSP Registrar a stock check may be narrowed
// to, searched and paged on the server (docs/shape-ops-stock-check-criteria.md
// D1, D3). AGE has no combobox; this is the shared DialogCombobox, whose look
// is in `stock-movement-dialog.css`.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudButton } from '@egov-moldova/mud-react';
import { DialogCombobox } from '@simsm/ui-shared';
import { searchImspRegistrars, type ImspRegistrar } from './api';

const PAGE_SIZE = 20;

function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

// The keyboard's "show more": reaching the last loaded Registrar loads the next
// page, because Tab closes the list before it reaches the footer's button.
function NextPageWhenActive({ active, onReach }: { active: boolean; onReach: () => void }): null {
  useEffect(() => {
    if (active) onReach();
  }, [active, onReach]);
  return null;
}

interface ImspRegistrarPickerProps {
  /** null: every IMSP Registrar. */
  selected: ImspRegistrar | null;
  ariaLabel: string;
  onPick: (registrar: ImspRegistrar | null) => void;
}

export function ImspRegistrarPicker({
  selected,
  ariaLabel,
  onPick,
}: ImspRegistrarPickerProps): JSX.Element {
  const { t } = useTranslate();
  // null: the field shows the choice; a string: what is being typed.
  const [draft, setDraft] = useState<string | null>(null);
  // Nothing is requested before the field is first used.
  const [engaged, setEngaged] = useState(false);
  const typed = (draft ?? '').trim();
  const term = useDebounced(typed, 200);
  const rootRef = useRef<HTMLDivElement>(null);
  const pointerInsideRef = useRef(false);

  // Leaving without a pick puts the chosen name back in the field: a search
  // left in it would read as the choice.
  useEffect(() => {
    function onPointerDown(event: PointerEvent): void {
      if (rootRef.current?.contains(event.target as Node)) return;
      pointerInsideRef.current = false;
      setDraft(null);
    }
    function onGestureEnd(): void {
      pointerInsideRef.current = false;
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('click', onGestureEnd, true);
    document.addEventListener('pointercancel', onGestureEnd, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('click', onGestureEnd, true);
      document.removeEventListener('pointercancel', onGestureEnd, true);
    };
  }, []);

  const { data, isError, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: ['uc92-imsp-registrar-page', term],
      queryFn: ({ pageParam }) =>
        searchImspRegistrars({ q: term, offset: pageParam, limit: PAGE_SIZE }),
      initialPageParam: 0,
      getNextPageParam: (last, all) => {
        const loaded = all.reduce((count, page) => count + page.items.length, 0);
        return loaded < last.total ? loaded : undefined;
      },
      enabled: engaged,
      placeholderData: keepPreviousData,
      staleTime: 5 * 60_000,
    });

  const registrars = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const total = data?.pages[data.pages.length - 1]?.total ?? 0;

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  function choose(next: ImspRegistrar | null): void {
    setDraft(null);
    onPick(next);
  }

  let status: ReactNode = null;
  if (isError) status = t('common.load_error');
  else if (isFetching && !isFetchingNextPage) status = t('crossImspStock.registrar_picker.loading');
  else if (engaged && registrars.length === 0 && term !== '')
    status = t('search_select.no_results');

  const footer =
    registrars.length > 0 ? (
      <span className="simsm-cross-imsp__picker-footer">
        <span>
          {t('crossImspStock.registrar_picker.shown', { shown: registrars.length, total })}
        </span>
        {hasNextPage ? (
          <MudButton
            shape="circular"
            appearance="text"
            variant="strict"
            size="sm"
            disabled={isFetchingNextPage}
            onClick={loadMore}
          >
            {t('crossImspStock.registrar_picker.more')}
          </MudButton>
        ) : null}
      </span>
    ) : null;

  const lastIdno = registrars[registrars.length - 1]?.idno;

  return (
    <div
      ref={rootRef}
      className="simsm-cross-imsp__picker"
      onFocus={() => setEngaged(true)}
      onPointerDown={() => {
        pointerInsideRef.current = true;
      }}
      onBlur={(event) => {
        if (pointerInsideRef.current) return;
        if (event.relatedTarget instanceof Node && rootRef.current?.contains(event.relatedTarget))
          return;
        setDraft(null);
      }}
    >
      <DialogCombobox<ImspRegistrar>
        size="md"
        value={draft ?? selected?.name ?? ''}
        onValueChange={(value) => {
          setEngaged(true);
          setDraft(value);
        }}
        groups={[{ key: 'registrars', options: registrars }]}
        optionKey={(registrar) => registrar.idno}
        optionLabel={(registrar) => registrar.name}
        renderOption={(registrar, { active }) => (
          <span className="simsm-cross-imsp__option">
            <span>{registrar.name}</span>
            <span className="simsm-cross-imsp__option-meta">
              {t('crossImspStock.registrar_picker.idno', { idno: registrar.idno })}
            </span>
            {hasNextPage && registrar.idno === lastIdno ? (
              <NextPageWhenActive active={active} onReach={loadMore} />
            ) : null}
          </span>
        )}
        onSelect={(registrar) => choose(registrar)}
        // While a search is typed the first option is its first match, which is
        // what Enter takes; "every Registrar" is offered on the unfiltered list.
        special={
          typed === ''
            ? {
                key: 'every-registrar',
                content: t('crossImspStock.filter.any_registrar'),
                onSelect: () => choose(null),
              }
            : undefined
        }
        listboxLabel={t('crossImspStock.registrar_picker.list_aria')}
        status={status}
        footer={footer}
        ariaLabel={ariaLabel}
        // With no Registrar chosen the field is empty, so typing starts a search,
        // and the placeholder states the choice in force.
        placeholder={
          selected
            ? t('crossImspStock.registrar_picker.placeholder')
            : t('crossImspStock.filter.any_registrar')
        }
      />
    </div>
  );
}
